import { easterSunday } from '../family/days.js';
import { jewishHolidaysOfYear } from '../faith/jodendom/holidays.js';
import { addDays, compareLocalDate, diffDays, parseLocalDate, weekdayOf, } from '../recurrence/localDate.js';
import { canonicalIslamicFixes, DIYANET_TABLES, eitherOrNl, islamicDates, isIslamicTable, isIslamicTradition, } from './islamic.js';
import { canonical, DAY_SETS, FAITHS, isTradition, KITCHENS, } from './vocab.js';
export const DAY_SET_DEFS = [
    {
        code: 'tr_national',
        kind: 'culture_day',
        offeredFor: { kitchens: ['turkish'] },
        days: [
            {
                key: 'tr_23_nisan',
                // Zelfde vorm als 19 Mayıs: de naam die het gezin kent eerst (review d r2).
                name: '23 Nisan (Kinderdag)',
                original: 'Ulusal Egemenlik ve Çocuk Bayramı',
                icon: 'play',
                rule: { kind: 'fixed', monthDay: '04-23' },
                kids: true,
            },
            {
                key: 'tr_19_mayis',
                // Herkenbare naam eerst (zoals het gezin hem kent), de uitleg tussen haakjes (review d-ux r1).
                name: '19 Mayıs (Jeugd- en sportdag)',
                original: 'Atatürk’ü Anma, Gençlik ve Spor Bayramı',
                icon: 'running',
                rule: { kind: 'fixed', monthDay: '05-19' },
                kids: true,
            },
            {
                key: 'tr_29_ekim',
                name: 'Cumhuriyet Bayramı (Dag van de Republiek)',
                original: '29 Ekim',
                icon: 'party',
                rule: { kind: 'fixed', monthDay: '10-29' },
            },
        ],
    },
    {
        // Nationale herdenking en viering van de afschaffing van de slavernij (1863): voor iedereen in
        // Nederland, niet alleen voor Surinaamse of Antilliaanse gezinnen. Wel standaard UIT.
        code: 'keti_koti',
        kind: 'culture_day',
        offeredFor: { everyone: true },
        days: [
            {
                key: 'keti_koti',
                name: 'Keti Koti',
                original: 'Keti Koti (Dag der Vrijheden)',
                icon: 'calendar',
                rule: { kind: 'fixed', monthDay: '07-01' },
                calm: true,
            },
        ],
    },
    {
        code: 'indies_remembrance',
        kind: 'culture_day',
        offeredFor: { kitchens: ['indonesian', 'moluccan'] },
        days: [
            {
                key: 'herdenking_15_augustus',
                name: 'Herdenking 15 augustus',
                original: 'Nationale Herdenking 15 augustus 1945',
                icon: 'calendar',
                rule: { kind: 'fixed', monthDay: '08-15' },
                calm: true,
            },
        ],
    },
    {
        // Alleen aangeboden bij Indisch/Indonesisch, NIET bij Moluks (gevoelig: RMS).
        code: 'id_independence',
        kind: 'culture_day',
        offeredFor: { kitchens: ['indonesian'] },
        days: [
            {
                key: 'id_17_agustus',
                name: 'Onafhankelijkheidsdag Indonesië',
                original: 'Hari Kemerdekaan (17 Agustus)',
                icon: 'party',
                rule: { kind: 'fixed', monthDay: '08-17' },
            },
        ],
    },
    {
        code: 'it_national',
        kind: 'culture_day',
        offeredFor: { kitchens: ['italian'] },
        days: [
            {
                key: 'it_festa_repubblica',
                name: 'Dag van de Republiek (Italië)',
                original: 'Festa della Repubblica',
                icon: 'party',
                rule: { kind: 'fixed', monthDay: '06-02' },
            },
        ],
    },
    {
        code: 'pl_national',
        kind: 'culture_day',
        offeredFor: { kitchens: ['polish'] },
        days: [
            {
                key: 'pl_3_maja',
                name: 'Dag van de Grondwet (3 Maja)',
                original: 'Święto Konstytucji 3 Maja',
                icon: 'party',
                rule: { kind: 'fixed', monthDay: '05-03' },
            },
            {
                key: 'pl_11_listopada',
                name: 'Onafhankelijkheidsdag (Polen)',
                original: 'Narodowe Święto Niepodległości',
                icon: 'party',
                rule: { kind: 'fixed', monthDay: '11-11' },
            },
        ],
    },
    {
        // Maandata per traditie (islamic.ts); "(verwacht)" alleen bij maanzicht en eigen moskee.
        code: 'islamic',
        kind: 'faith_day',
        offeredFor: { faiths: ['islam'], kitchens: ['turkish', 'moroccan'] },
        days: [
            {
                key: 'ramadan_start',
                name: 'Begin Ramadan',
                original: 'Ramazan / Ramadan',
                icon: 'moon',
                rule: { kind: 'table', table: 'ramadan_start' },
            },
            {
                key: 'ramadan_end',
                name: 'Laatste dag Ramadan',
                original: null,
                icon: 'moon',
                rule: { kind: 'table', table: 'eid_al_fitr', offset: -1 },
            },
            {
                // Golf 2: overal "Eid al-Fitr (Suikerfeest)" (Engels: "Eid al-Fitr").
                key: 'eid_al_fitr',
                name: 'Eid al-Fitr (Suikerfeest)',
                original: 'Ramazan Bayramı',
                icon: 'party',
                rule: { kind: 'table', table: 'eid_al_fitr' },
                length: 3,
                kids: true,
            },
            {
                key: 'eid_al_adha',
                name: 'Eid al-Adha (Offerfeest)',
                original: 'Kurban Bayramı',
                icon: 'party',
                rule: { kind: 'table', table: 'eid_al_adha' },
                length: 4,
            },
            {
                key: 'islamic_new_year',
                name: 'Islamitisch nieuwjaar',
                original: 'Hicri Yılbaşı / Ras as-Sana',
                icon: 'calendar',
                rule: { kind: 'table', table: 'islamic_new_year' },
            },
            {
                // Niet iedere moslim viert dit; daarom per dag uit te zetten (zoals elke dag).
                key: 'mawlid',
                name: 'Mawlid (Mevlid)',
                original: 'Mevlid Kandili / Mawlid an-Nabi',
                icon: 'moon',
                rule: { kind: 'table', table: 'mawlid' },
            },
        ],
    },
    {
        // Goede Vrijdag, Pasen, Hemelvaart, Pinksteren en Kerst blijven Nederlandse feestdagen voor
        // iedereen (dutchDays). Deze set voegt alleen dagen toe die geen vrije dag zijn.
        code: 'christian',
        kind: 'faith_day',
        offeredFor: { faiths: ['christianity'] },
        days: [
            {
                key: 'driekoningen',
                name: 'Driekoningen',
                original: null,
                icon: 'gift',
                rule: { kind: 'fixed', monthDay: '01-06' },
            },
            {
                key: 'aswoensdag',
                name: 'Aswoensdag',
                original: null,
                icon: 'calendar',
                rule: { kind: 'easter', offset: -46 },
                calm: true,
            },
            {
                key: 'palmzondag',
                name: 'Palmzondag',
                original: null,
                icon: 'calendar',
                rule: { kind: 'easter', offset: -7 },
            },
            {
                key: 'witte_donderdag',
                name: 'Witte Donderdag',
                original: null,
                icon: 'calendar',
                rule: { kind: 'easter', offset: -3 },
                calm: true,
            },
            {
                key: 'allerheiligen',
                name: 'Allerheiligen',
                original: null,
                icon: 'calendar',
                rule: { kind: 'fixed', monthDay: '11-01' },
                calm: true,
            },
            {
                key: 'eerste_advent',
                name: 'Eerste Advent',
                original: null,
                icon: 'calendar',
                rule: { kind: 'advent' },
            },
        ],
    },
    {
        // Uitgerekend met de eigen joodse kalender (faith/jodendom; module Jodendom, 0062). Lengtes van
        // de diaspora, of van Israël als het gezin dat koos (DayChoice.jewish). De sleutels van vóór
        // 0062 (poeriem t/m chanoeka) blijven gelijk, zodat een uitgezette dag uit blijft. Begint de
        // avond ervoor (behalve de kleine vastendagen: die beginnen bij het ochtendgloren).
        // LET OP (review): namen en uitleg vóór de publieke lancering laten nakijken door een rabbijn
        // of een lid van de gemeenschap.
        code: 'jewish',
        kind: 'faith_day',
        offeredFor: { faiths: ['judaism'] },
        days: [
            {
                key: 'rosj_hasjana',
                name: 'Rosj Hasjana',
                original: 'Rosh Hashanah',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'rosh_hashana' },
                eveBefore: true,
            },
            {
                key: 'tsom_gedalja',
                name: 'Tsom Gedalja (vastendag)',
                original: 'Tzom Gedaliah',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'tzom_gedalia' },
                calm: true,
            },
            {
                key: 'jom_kipoer',
                name: 'Jom Kipoer',
                original: 'Yom Kippur',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'yom_kippur' },
                calm: true,
                eveBefore: true,
            },
            {
                key: 'soekot',
                name: 'Soekot',
                original: 'Sukkot',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'sukkot' },
                eveBefore: true,
            },
            {
                key: 'sjemini_atseret',
                name: 'Sjemini Atseret',
                original: 'Shemini Atzeret',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'shemini_atzeret' },
                eveBefore: true,
            },
            {
                key: 'simchat_tora',
                name: 'Simchat Tora',
                original: 'Simchat Torah',
                icon: 'party',
                rule: { kind: 'hebrew', holiday: 'simchat_torah' },
                kids: true,
                eveBefore: true,
            },
            {
                key: 'chanoeka',
                name: 'Chanoeka',
                original: 'Chanukah',
                icon: 'party',
                rule: { kind: 'hebrew', holiday: 'chanukah' },
                kids: true,
                eveBefore: true,
            },
            {
                key: 'asara_betevet',
                name: 'Asara Betevet (vastendag)',
                original: "Asara B'Tevet",
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'asara_btevet' },
                calm: true,
            },
            {
                key: 'toe_bisjvat',
                name: 'Toe Bisjvat',
                original: 'Tu BiShvat',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'tu_bishvat' },
                eveBefore: true,
            },
            {
                key: 'taaniet_esther',
                name: 'Taaniet Esther (vastendag)',
                original: "Ta'anit Esther",
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'taanit_esther' },
                calm: true,
            },
            {
                key: 'poeriem',
                name: 'Poeriem',
                original: 'Purim',
                icon: 'party',
                rule: { kind: 'hebrew', holiday: 'purim' },
                kids: true,
                eveBefore: true,
            },
            {
                key: 'pesach',
                name: 'Pesach',
                original: 'Pesach',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'pesach' },
                eveBefore: true,
            },
            {
                key: 'lag_baomer',
                name: 'Lag Baomer',
                original: 'Lag BaOmer',
                icon: 'party',
                rule: { kind: 'hebrew', holiday: 'lag_baomer' },
                kids: true,
                eveBefore: true,
            },
            {
                key: 'sjavoeot',
                name: 'Sjavoeot',
                original: 'Shavuot',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'shavuot' },
                eveBefore: true,
            },
            {
                key: 'tsom_tammoez',
                name: '17 Tamoez (vastendag)',
                original: 'Tzom Tammuz',
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'tzom_tammuz' },
                calm: true,
            },
            {
                key: 'tisja_beav',
                name: 'Tisja Beav (vastendag)',
                original: "Tish'a B'Av",
                icon: 'calendar',
                rule: { kind: 'hebrew', holiday: 'tisha_bav' },
                calm: true,
                eveBefore: true,
            },
        ],
    },
    {
        // Maandata: tabel per jaar (builder B, bron in 0024-werk); "(verwacht)" in de titel.
        code: 'hindu',
        kind: 'faith_day',
        offeredFor: { faiths: ['hinduism'] },
        days: [
            {
                key: 'holi',
                name: 'Holi (Phagwa)',
                original: 'Holi / Phagwa',
                icon: 'party',
                rule: { kind: 'table', table: 'holi' },
                kids: true,
            },
            {
                key: 'diwali',
                name: 'Diwali',
                original: 'Diwali / Divali',
                icon: 'party',
                rule: { kind: 'table', table: 'diwali' },
            },
        ],
    },
];
/** Eerste jaar en laatste jaar waarvoor DAY_TABLES volledig is (test op volledigheid). */
export const DAY_TABLE_YEARS = { from: 2026, to: 2032 };
/**
 * Maandata per jaar ('MM-DD', of een lijst als iets twee keer in één jaar valt: de islamitische
 * kalender is ~11 dagen korter, dus in 2030 begint de Ramadan in januari én in december). Ontbreekt
 * een jaar, dan komt die dag er niet in (nooit een gok). Alles hier is "(verwacht)": de precieze
 * dag kan een dag verschuiven (maanwaarneming, lokale kalender).
 *
 * Bronnen (geraadpleegd 2026-09-24):
 * - Islamitisch: Diyanet İşleri Başkanlığı, "Dini Günler" (hesaplamaya dayalı takvim, zoals
 *   overgenomen door Hollanda Diyanet Vakfı en vaktime.com/dini-gunler/<jaar>). `ramadan_start` =
 *   Ramazan başlangıcı (eerste vastendag), `eid_al_fitr` en `eid_al_adha` = 1e dag van de bayram,
 *   `islamic_new_year` = Hicri Yılbaşı (1 Muharrem), `mawlid` = Mevlid Kandili (de avond vóór
 *   12 Rebiülevvel, zoals Turks-Nederlandse gezinnen hem vieren).
 * - Hindoeïstisch: Holi Phagwa en Divali zoals de Surinaamse feestdagenkalender ze vastlegt
 *   (Phagwa op de volle maan van Phalgun; Divali = Lakshmi Puja), bron: qppstudio.net
 *   "Holi Phagwa" en "Deepavali/Diwali" (officiële feestdag in Suriname), gecontroleerd tegen de
 *   Nederlandse Hindoestaanse kalenders voor 2026-2028.
 */
export const DAY_TABLES = {
    // Golf 2: de islamitische tabellen staan in islamic.ts (Diyanet 2026-2035 en Umm al-Qura, met
    // bron). Hier het vaste rooster als standaard (zonder traditie). Mawlid 2030-2032 volgt nu de
    // officiële tabel (11 Rebiülevvel, de kandilavond); de oude waarden lagen een dag later.
    ramadan_start: DIYANET_TABLES.ramadan_start,
    eid_al_fitr: DIYANET_TABLES.eid_al_fitr,
    eid_al_adha: DIYANET_TABLES.eid_al_adha,
    islamic_new_year: DIYANET_TABLES.islamic_new_year,
    mawlid: DIYANET_TABLES.mawlid,
    diwali: {
        2026: '11-08',
        2027: '10-29',
        2028: '10-17',
        2029: '11-05',
        2030: '10-26',
        2031: '11-14',
        2032: '11-02',
    },
    holi: {
        2026: '03-03',
        2027: '03-22',
        2028: '03-11',
        2029: '03-01',
        2030: '03-20',
        2031: '03-09',
        2032: '03-27',
    },
};
/** Alle maandata van een tabel in een jaar (0, 1 of 2 keer). */
export function tableDates(table, year) {
    const v = DAY_TABLES[table][year];
    if (v === undefined)
        return [];
    return typeof v === 'string' ? [v] : v.slice();
}
export const NO_DAYS = Object.freeze({ daySets: [], dayKeysOff: [] });
/**
 * Dagen van `year` voor de AANGEZETTE sets, zonder de uitgezette dagen. Meerdaagse dagen komen
 * één keer per dag (dagIndex/dagCount), ook als ze over de jaargrens lopen (dan in het jaar waarin
 * ze beginnen). Gesorteerd op datum, dan sleutel.
 */
export function profileDays(year, choice) {
    const sets = canonical(choice.daySets, DAY_SETS);
    if (sets.length === 0)
        return [];
    const off = choice.dayKeysOff;
    const out = [];
    for (const def of DAY_SET_DEFS) {
        if (sets.indexOf(def.code) < 0)
            continue;
        for (const day of def.days) {
            if (off.indexOf(day.key) >= 0)
                continue;
            for (const st of startsOf(day.rule, year, choice.islamic, choice.jewish)) {
                const count = st.count ?? day.length ?? 1;
                for (let i = 0; i < count; i += 1) {
                    out.push({
                        date: addDays(st.date, i),
                        key: day.key,
                        set: def.code,
                        kind: def.kind,
                        name: day.name,
                        original: day.original,
                        icon: day.icon,
                        kids: day.kids === true,
                        calm: day.calm === true,
                        expected: st.expected,
                        eveBefore: day.eveBefore === true,
                        tradition: st.tradition,
                        alternative: st.alternative && i === 0 ? st.alternative : null,
                        confirmed: st.confirmed,
                        fixKey: st.fixKey,
                        dayIndex: i + 1,
                        dayCount: count,
                    });
                }
            }
        }
    }
    return out.sort((a, b) => compareLocalDate(a.date, b.date) || (a.key < b.key ? -1 : 1));
}
/** Dagen tussen `from` en `to` (inclusief), over jaargrenzen heen. */
export function profileDaysBetween(from, to, choice) {
    const y0 = parseLocalDate(from).year;
    const y1 = parseLocalDate(to).year;
    const out = [];
    // Een jaar eerder erbij: een meerdaagse dag die in december begint, loopt door in januari.
    for (let y = y0 - 1; y <= y1; y += 1) {
        for (const d of profileDays(y, choice)) {
            if (compareLocalDate(d.date, from) >= 0 && compareLocalDate(d.date, to) <= 0)
                out.push(d);
        }
    }
    return out;
}
/**
 * Voorvertoning bij een set ("Eerstvolgende: 23 apr, 19 mei, 29 okt"): de eerste `n` hoofddagen
 * vanaf `from` (meerdaagse dagen tellen één keer). Voor een set die nog UIT staat.
 */
export function previewDaySet(set, from, n = 3, islamic) {
    const y0 = parseLocalDate(from).year;
    const out = [];
    for (let y = y0; y <= y0 + 2 && out.length < n; y += 1) {
        for (const d of profileDays(y, {
            daySets: [set],
            dayKeysOff: [],
            ...(islamic ? { islamic } : {}),
        })) {
            if (d.dayIndex !== 1 || compareLocalDate(d.date, from) < 0)
                continue;
            out.push(d);
            if (out.length >= n)
                break;
        }
    }
    return out;
}
/**
 * Welke sets we AANBIEDEN (open rij met schakelaar, standaard UIT), in deze volgorde: sets bij het
 * gekozen geloof, dan bij de keukens/tradities, dan sets voor iedereen. De rest staat onder "Meer
 * bijzondere dagen" (allDaySets). Aanbieden zet nooit iets aan.
 */
export function offeredDaySets(input) {
    const faiths = canonical(input.faiths ?? [], FAITHS);
    const kitchens = canonical([...input.kitchens, ...(input.traditions ?? []).filter((t) => isTradition(t))], KITCHENS);
    const byFaith = [];
    const byKitchen = [];
    const forAll = [];
    for (const def of DAY_SET_DEFS) {
        const o = def.offeredFor;
        // Geloofsdagen via een keuken alleen zolang geloof open is: na "Geen geloof" of een ander
        // geloof bieden we geen islamitische dagen aan omdat er Turks gekozen is.
        const kitchenCounts = def.kind === 'culture_day' || input.faiths === null;
        if (o.faiths && o.faiths.some((f) => faiths.indexOf(f) >= 0))
            byFaith.push(def.code);
        else if (kitchenCounts && o.kitchens && o.kitchens.some((k) => kitchens.indexOf(k) >= 0))
            byKitchen.push(def.code);
        else if (o.everyone === true)
            forAll.push(def.code);
    }
    return [...byFaith, ...byKitchen, ...forAll];
}
/** Uitleg bij een bijzondere dag (Nederlands; zoals de rest van de voorgeformatteerde dagen). */
export const PROFILE_DAY_TEXT = {
    expected: '(verwacht)',
    expectedNote: 'De precieze dag kan een dag verschuiven.',
    eveBefore: 'Begint de avond ervoor bij zonsondergang.',
    // Golf 2: uitleg per traditie (islamic.ts). {dates} = "vr 20 of za 21 mrt".
    saudi: 'Maanzicht in Saoedi-Arabië: {dates}. De bevestiging komt de avond ervoor.',
    morocco: 'Maanzicht in Marokko: {dates}. De bevestiging komt de avond ervoor.',
    ownMosque: 'Jullie moskee kan een dag eerder of later kiezen.',
};
/** Titel zoals de agenda en de kiosk hem tonen: "Suikerfeest (verwacht)". */
export function profileDayTitle(d) {
    return d.expected ? `${d.name} ${PROFILE_DAY_TEXT.expected}` : d.name;
}
/** Uitleg bij een dag ("Begint de avond ervoor ...", "kan een dag verschuiven"), of null. */
export function profileDayDetail(d) {
    const parts = [];
    if (d.eveBefore && d.dayIndex === 1)
        parts.push(PROFILE_DAY_TEXT.eveBefore);
    if (d.tradition !== null) {
        // Islamitische dag: uitleg bij de traditie (alleen op de eerste dag), nooit bij Diyanet.
        if (d.expected && d.dayIndex === 1) {
            if (d.tradition === 'own_mosque')
                parts.push(PROFILE_DAY_TEXT.ownMosque);
            else if (d.tradition === 'saudi' || d.tradition === 'morocco') {
                const text = d.tradition === 'saudi' ? PROFILE_DAY_TEXT.saudi : PROFILE_DAY_TEXT.morocco;
                parts.push(text.replace('{dates}', eitherOrNl(d.date, d.alternative)));
            }
        }
    }
    else if (d.expected)
        parts.push(PROFILE_DAY_TEXT.expectedNote);
    return parts.length > 0 ? parts.join(' ') : null;
}
/** Een aangezette bijzondere dag als dagmarkering (highlights; web, kiosk). */
export function profileDayHighlight(d) {
    return {
        kind: d.kind,
        title: profileDayTitle(d),
        icon: d.icon,
        member_id: null,
        color_key: null,
        age: null,
        day_index: d.dayIndex,
        day_count: d.dayCount,
        household_label: null,
        detail: profileDayDetail(d),
        base_title: d.name,
        expected: d.expected,
        alternative_date: d.alternative,
        fixable: d.expected && d.fixKey !== null,
    };
}
/** Alle sets, voor "Meer bijzondere dagen" (niets verbergen: alles is vindbaar). */
export function allDaySets() {
    return DAY_SET_DEFS.map((d) => d.code);
}
export function daySetDef(code) {
    const def = DAY_SET_DEFS.find((d) => d.code === code);
    if (!def)
        throw new Error(`onbekende set ${code}`);
    return def;
}
/** Alle dagsleutels (voor validatie van day_keys_off). */
export const DAY_KEYS = allDayKeys();
function allDayKeys() {
    // Geen flatMap: de kiosk typecheckt dit bestand met ES2018.
    const out = [];
    for (const set of DAY_SET_DEFS)
        for (const d of set.days)
            out.push(d.key);
    return out;
}
/**
 * Canonieke keuze: onbekende sets en sleutels weg, en een uitgezette dag telt alleen binnen een
 * aangezette set (anders is hij overbodig: de hele set staat al uit). Zo blijft er na "set uit"
 * niets achter in day_keys_off (minimalisatie).
 */
export function canonicalDayChoice(choice) {
    const sets = canonical(choice.daySets, DAY_SETS);
    const allowed = [];
    for (const def of DAY_SET_DEFS) {
        if (sets.indexOf(def.code) >= 0)
            for (const d of def.days)
                allowed.push(d.key);
    }
    const out = {
        daySets: sets,
        dayKeysOff: allowed.filter((k) => choice.dayKeysOff.indexOf(k) >= 0),
    };
    // De traditie telt alleen zolang de islamitische set aan staat.
    const isl = choice.islamic;
    if (isl && sets.indexOf('islamic') >= 0 && isIslamicTradition(isl.tradition)) {
        out.islamic = {
            tradition: isl.tradition,
            fixes: canonicalIslamicFixes(isl.fixes, isl.tradition),
            ...(isl.settledBefore !== undefined ? { settledBefore: isl.settledBefore } : {}),
        };
    }
    return out;
}
/** Eerste dagen van een joodse feestdag die in gewoon jaar `year` beginnen. */
function hebrewStarts(holiday, year, israel) {
    const out = [];
    const y = String(year).padStart(4, '0');
    // Een gewoon jaar raakt twee joodse jaren (Rosj Hasjana valt in september of oktober).
    for (const hy of [year + 3760, year + 3761]) {
        for (const d of jewishHolidaysOfYear(hy, { israel })) {
            if (d.key !== holiday || d.dayIndex !== 1 || d.date.slice(0, 4) !== y)
                continue;
            out.push({
                date: d.date,
                expected: false,
                tradition: null,
                alternative: null,
                confirmed: false,
                fixKey: null,
                count: d.dayCount,
            });
        }
    }
    return out;
}
/**
 * Startdata van een regel met hun status. Islamitische tabellen volgen de traditie van het gezin
 * (islamic.ts; zonder keuze het vaste rooster); andere tabellen (hindoeïstisch) blijven
 * "(verwacht)".
 */
function startsOf(rule, year, islamic, jewish) {
    if (rule.kind === 'hebrew')
        return hebrewStarts(rule.holiday, year, jewish?.israel === true);
    if (rule.kind === 'table' && isIslamicTable(rule.table)) {
        const choice = islamic ?? { tradition: 'diyanet', fixes: {} };
        const offset = rule.offset ?? 0;
        return islamicDates(rule.table, year, choice).map((d) => ({
            date: addDays(d.date, offset),
            expected: d.expected,
            tradition: choice.tradition,
            alternative: d.alternative ? addDays(d.alternative, offset) : null,
            confirmed: d.confirmed,
            fixKey: d.fixKey,
        }));
    }
    return dayDates(rule, year).map((date) => ({
        date,
        expected: rule.kind === 'table',
        tradition: null,
        alternative: null,
        confirmed: false,
        fixKey: null,
    }));
}
/** Alle startdata van een regel in `year` (een tabel kan er twee hebben, zie DAY_TABLES). */
function dayDates(rule, year) {
    if (rule.kind === 'table') {
        const y = String(year).padStart(4, '0');
        return tableDates(rule.table, year).map((md) => addDays(`${y}-${md}`, rule.offset ?? 0));
    }
    const d = dayDate(rule, year);
    return d === null ? [] : [d];
}
function dayDate(rule, year) {
    const y = String(year).padStart(4, '0');
    switch (rule.kind) {
        case 'fixed':
            return `${y}-${rule.monthDay}`;
        case 'easter':
            return addDays(easterSunday(year), rule.offset);
        case 'advent': {
            // Vierde zondag vóór Kerst: de zondag in 27 november t/m 3 december.
            let d = `${y}-11-27`;
            while (weekdayOf(d) !== 'SU')
                d = addDays(d, 1);
            return d;
        }
        case 'jewish': {
            const anchor = rule.anchor === 'pesach'
                ? pesachDate(year)
                : rule.anchor === 'rosh'
                    ? roshHashanahDate(year)
                    : chanukahDate(year);
            return addDays(anchor, rule.offset);
        }
        case 'table':
            return dayDates(rule, year)[0] ?? null;
        case 'hebrew':
            return hebrewStarts(rule.holiday, year, false)[0]?.date ?? null;
    }
}
/**
 * Eerste dag van Pesach (15 Nisan) in de gregoriaanse kalender, formule van Gauss. Geverifieerd
 * tegen bekende data (2019-2033) in profile.test.ts.
 */
export function pesachDate(year) {
    const a = (12 * year + 12) % 19;
    const b = year % 4;
    const q = -1.904412361576 + 1.554241796621 * a + 0.25 * b - 0.003177794022 * year;
    const fq = Math.floor(q);
    const r = q - fq;
    const j = (fq + 3 * year + 5 * b + 2) % 7;
    let d;
    if (j === 2 || j === 4 || j === 6)
        d = fq + 23;
    else if (j === 1 && a > 6 && r >= 0.63287037)
        d = fq + 24;
    else if (j === 0 && a > 11 && r >= 0.897723765)
        d = fq + 23;
    else
        d = fq + 22;
    // d = dag in maart (juliaans); naar gregoriaans.
    d += Math.floor(year / 100) - Math.floor(year / 400) - 2;
    return addDays(`${String(year).padStart(4, '0')}-03-01`, d - 1);
}
/** Rosj Hasjana (1 Tisjri) van het joodse jaar dat in de herfst van `year` begint. */
export function roshHashanahDate(year) {
    // Van 15 Nisan tot 1 Tisjri is altijd 163 dagen (vaste maanden Nisan t/m Elul).
    return addDays(pesachDate(year), 163);
}
/** Eerste dag van Chanoeka (25 Kislev) na Rosj Hasjana van `year`. */
export function chanukahDate(year) {
    const rosh = roshHashanahDate(year);
    const length = diffDays(rosh, roshHashanahDate(year + 1));
    // Cheshvan heeft 30 dagen in een "volledig" jaar (355 of 385 dagen), anders 29.
    const cheshvan = length % 10 === 5 ? 30 : 29;
    return addDays(rosh, 30 + cheshvan + 24);
}
//# sourceMappingURL=days.js.map